<template>
  <div id="mapbox">
    <div id="mapOverlay">
      <section class="mapLegend">
        <div class="legend-item">
          <img src="./markers/marker-red.png"
            alt="Enabled Location">
          <span>Enabled Location</span>
        </div>
        <div class="legend-item">
          <img src="./markers/marker-grey.png"
            alt="Extended Location">
          <span>Extended Location</span>
        </div>
      </section>
      <button v-if="!options.closeOnClick"
        data-name="mapbox-close-popovers-btn"
        type="button"
        @click="closeAllPopovers">
        Close all popups
      </button>
      <button type="button"
        data-name="mapbox-reset-zoom-btn"
        @click="resetZoom">
        Reset zoom
      </button>
    </div>
    <div id="mapboxContainer" />
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import enabledMapPin from './markers/marker-red.png'
import extendedMapPin from './markers/marker-grey.png'

export default {
  name: 'MapBox',

  props: {
    geopoints: {
      type: Object,
      required: false,
      default: undefined,
    },
    geopaths: {
      type: Object,
      required: false,
      default: undefined,
    },
    showlayers: {
      type: Object,
      required: false,
      default: undefined,
    },
    options: {
      type: Object,
      required: false,
      default: undefined,
    },
  },

  data: () => {
    return {
      map: null,
      mapboxStyle: import.meta.env.VITE_MAPBOX_STYLE,
      mapboxToken: import.meta.env.VITE_MAPBOX_TOKEN,
      currentPopover: {},
      pathLayerIds: [],
      preventPopup: false,
      defaultZoom: 1.8,
    }
  },

  watch: {
    showlayers(layers) {
      Object.keys(layers).forEach(layerId => {
        const visibility = layers[layerId] ? 'visible' : 'none'
        this.map.setLayoutProperty(layerId, 'visibility', visibility)
      })
    },
    geopoints(points) {
      this.map.getSource('points').setData(points.source.data)
      this.closeAllPopovers()
    },
  },

  mounted() {
    this.initMap()
  },

  methods: {
    initMap() {
      this.defaultZoom = this.options.zoom ? this.options.zoom : this.defaultZoom
      mapboxgl.accessToken = this.mapboxToken
      this.map = new mapboxgl.Map({
        container: 'mapboxContainer',
        style: this.mapboxStyle,
        center: [150, 10],
        zoom: this.defaultZoom,
      })
      const nav = new mapboxgl.NavigationControl({ showCompass: false })
      this.map.addControl(nav, 'top-left')
      this.map.on('load', this.loadImages)
    },
    loadImages() {
      this.map.loadImage(enabledMapPin, (error, enabledImage) => {
        if (error) throw error
        this.map.addImage('enabledMapPin', enabledImage)
      })

      this.map.loadImage(extendedMapPin, (error, extendedImage) => {
        if (error) throw error
        this.map.addImage('extendedMapPin', extendedImage)
      })
      this.addSources()
    },
    addSources() {
      // paths
      if (this.geopaths) {
        this.map.addSource('paths', {
          type: 'geojson',
          ...this.geopaths.source,
        })
        this.addPathLayer()
        this.addPathEvents()
      }
      // points
      if (this.geopoints) {
        this.map.addSource('points', {
          type: 'geojson',
          ...this.geopoints.source,
        })
        this.addPointsLayer()
        this.addPointEvents()
        this.addClusterEvents()
        this.zoomToPoint()
      }
    },
    zoomToPoint() {
      if (this.options.zoomToPoint) {
        const properties = this.geopoints.source.data.properties
        this.map.jumpTo({ center: this.options.zoomCoords, zoom: 12 })
        this.map.panBy([0, -90])
        const popupObj = {
          popup: properties.popup,
          coordinates: this.options.zoomCoords,
          closeOnClick: true,
          id: `location-${properties.id}`,
        }
        this.showPopover(popupObj)
      }
    },
    addPointsLayer() {
      // Cluster shape
      if (this.geopoints.clusterShape) {
        this.map.addLayer({
          id: 'clusterShape',
          source: 'points',
          ...this.geopoints.clusterShape,
        })
      }
      // Cluster text
      if (this.geopoints.clusterText) {
        this.map.addLayer({
          id: 'clusterText',
          source: 'points',
          ...this.geopoints.clusterText,
        })
      }
      // Pin points
      if (this.geopoints.pins) {
        this.map.addLayer({
          id: 'pointsLayer',
          source: 'points',
          layout: {
            'icon-image': [
              'case',
              ['==', ['get', 'status'], 'Extended'],
              'extendedMapPin',
              'enabledMapPin',
            ],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
          },
          ...this.geopoints.pins,
        })
      }
    },
    addPathLayer() {
      if (Array.isArray(this.geopaths.layers)) {
        this.geopaths.layers.forEach(layer => {
          this.pathLayerIds.push(layer.id)
          this.map.addLayer({
            id: layer.id,
            type: 'line',
            source: 'paths',
            ...layer,
          })
        })
      }
    },
    addClusterEvents() {
      this.map.on('mouseenter', 'clusterText', () => {
        this.map.getCanvas().style.cursor = 'pointer'
      })
      this.map.on('mouseleave', 'clusterText', () => {
        this.map.getCanvas().style.cursor = ''
      })
      this.map.on('click', 'clusterText', event => {
        this.preventPopup = true
        const features = this.map.queryRenderedFeatures(event.point, {
          layers: ['clusterShape'],
        })
        const clusterId = features[0].properties.cluster_id
        this.map.getSource('points').getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return
          setTimeout(() => {
            this.map.easeTo({
              center: features[0].geometry.coordinates,
              zoom: zoom + 5,
            })
            this.closeAllPopovers()
            this.preventPopup = false
          }, 100)
        })
      })
    },
    addPathEvents() {
      const pathLayerIds = this.geopaths.layers.map(layer => layer.id)

      this.pathLayerIds.forEach(layerId => {
        this.map.on('mouseenter', layerId, () => {
          this.map.getCanvas().style.cursor = 'pointer'
        })
        this.map.on('click', layerId, event => {
          const ax = event.point.x - 10 / 2
          const ay = event.point.y - 10 / 2
          const bx = event.point.x + 10 / 2
          const by = event.point.y + 10 / 20
          const features = this.map.queryRenderedFeatures(
            [
              [ax, ay],
              [bx, by],
            ],
            { layers: pathLayerIds }
          )
          this.generatePointPopup({
            ...event,
            features: [...new Set(features)],
          })
        })
        this.map.on('mouseleave', layerId, () => {
          this.map.getCanvas().style.cursor = ''
        })
      })
    },
    addPointEvents() {
      this.map.on('click', 'pointsLayer', event => {
        this.generatePointPopup(event)
      })
      this.map.on('mouseenter', 'pointsLayer', () => {
        this.map.getCanvas().style.cursor = 'pointer'
      })
      this.map.on('mouseleave', 'pointsLayer', () => {
        this.map.getCanvas().style.cursor = ''
      })
    },
    generatePointPopup(event) {
      const properties = event.features[0].properties
      const source = event.features[0].source
      const sourceIDs = []
      const popup = []
      event.features.forEach(feature => {
        if (!sourceIDs.includes(feature.properties.id)) {
          sourceIDs.push(feature.properties.id)
          popup.push(feature.properties.popup)
        }
      })
      if (popup.length > 3)
        popup.unshift(`<span><strong>${popup.length} connections at intersection.</strong><br>Scroll to reveal others in this list.</span>`)
      const closeOnClick = properties.closeOnClick ? properties.closeOnClick : false
      const lat = properties.latitude ? properties.latitude : event.lngLat.lat
      const lng = properties.longitude ? properties.longitude : event.lngLat.lng
      const popupObj = {
        popup: popup,
        coordinates: [lng, lat],
        closeOnClick: closeOnClick,
        id: `${source}-${properties.id}`,
        anchor: typeof properties.a_end_location_id !== 'undefined' ? 'right' : this.options.popoverAnchor,
      }
      setTimeout(() => {
        this.showPopover(popupObj)
      }, 100)
    },
    doesPopoverExist(popupObj) {
      const id = `${popupObj.coordinates[1]}-${popupObj.coordinates[0]}`
      return typeof this.currentPopover[id] !== 'undefined'
    },
    showPopover(popupObj) {
      if (!this.preventPopup && !this.doesPopoverExist(popupObj)) {
        const popupHtml = Array.isArray(popupObj.popup) ? popupObj.popup.join('<hr/>') : popupObj.popup
        const popup = new mapboxgl.Popup({
          closeOnClick: this.options.closeOnClick,
          maxWidth: '350px',
          offset: 20,
          className: 'mapPopup',
          anchor: popupObj.anchor || 'bottom',
        })
        popup.setHTML(popupHtml)
        popup.on('open', this.openPopoverHandler)
        popup.on('close', this.closePopoverHandler)
        popup.setLngLat(popupObj.coordinates)
        popup.addTo(this.map)
      }
    },
    openPopoverHandler(event) {
      const { lat, lng } = event.target.getLngLat()
      const id = `${lat}-${lng}`
      if (this.options.centrePoint) this.map.panTo([lng, lat])
      this.currentPopover[id] = event.target
    },
    closePopoverHandler(event) {
      const { lat, lng } = event.target.getLngLat()
      const id = `${lat}-${lng}`
      delete this.currentPopover[id]
    },
    closeAllPopovers() {
      Object.keys(this.currentPopover).forEach(popover => {
        this.currentPopover[popover].remove()
      })
      this.currentPopover = {}
    },
    resetZoom() {
      this.map.setZoom(this.defaultZoom)
    },
  },
}
</script>

<style lang="scss" scoped>
#mapbox {
  height: 100%;
  width: 100%;
  position: relative;
  #mapboxContainer {
    width: 100%;
    height: 100%;
  }
}

#mapOverlay {
  position: absolute;
  z-index: 10;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 1rem;
  .mapLegend {
    display: flex;
    gap: 12px;
    block-size: fit-content;
    background-color: var(--box-bg, white);
    color: var(--text-primary, #282828);
    border-radius: 8px;
    padding: 4px 8px;
    .legend-item {
      display: flex;
      align-items: center;
      gap: 4px;
      img {
        height: 20px;
        width: auto;
      }
    }
  }
  button {
    background-color: var(--box-bg, #ffffff);
    border: 1px solid var(--border-color, rgb(224, 224, 224));
    color: var(--text-primary, rgb(40, 40, 40));
    padding: 1rem;
  }
}

#mapboxContainer::v-deep .mapPopup {
  color: var(--text-primary, rgb(40, 40, 40));

  .mapboxgl-popup-content {
    background-color: var(--box-bg, #ffffff);
    min-width: 250px;
    max-height: 600px;
    overflow-y: auto;
    position: relative;

    .mapPopupTitle {
      color: inherit;
      font-weight: bold;
    }

    h3 .mapPopupTitle {
      font-size: 1.1rem;
    }

    a {
      color: var(--link-color, #0074d9);
      text-decoration: underline;

      .mapPopupTitle {
        color: inherit;
      }
    }

    hr {
      border: 0;
      border-top: 1px solid var(--border-medium, rgb(125, 125, 125));
    }
  }

  .mapboxgl-popup-close-button {
    color: inherit;
  }

  &.mapboxgl-popup-anchor-top .mapboxgl-popup-tip,
  &.mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip,
  &.mapboxgl-popup-anchor-top-right .mapboxgl-popup-tip {
    border-bottom-color: var(--box-bg, #ffffff);
  }

  &.mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip,
  &.mapboxgl-popup-anchor-bottom-left .mapboxgl-popup-tip,
  &.mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip {
    border-top-color: var(--box-bg, #ffffff);
  }

  &.mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
    border-right-color: var(--box-bg, #ffffff);
  }

  &.mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
    border-left-color: var(--box-bg, #ffffff);
  }
}

#mapboxContainer::v-deep .mapboxgl-ctrl-group {
  background-color: var(--box-bg, #ffffff);

  button {
    background-color: var(--box-bg, #ffffff);
    border-color: var(--border-color, rgb(224, 224, 224));

    .mapboxgl-ctrl-icon {
      filter: var(--mapbox-control-icon-filter, none);
    }
  }
}
</style>

<style lang="scss">
@import 'mapbox-gl/dist/mapbox-gl.css';

#bigMapBtn button {
  background-color: var(--box-bg, #ffffff);
  border: 1px solid var(--border-color, rgb(224, 224, 224));
  color: var(--text-primary, rgb(40, 40, 40));
}
</style>
